import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import LineItemPriceField from '@/ui/components/addEditItemSidebar/LineItemPriceField';
import { Field as SwitchGroup, Label as SwitchLabel } from '@headlessui/react';
import { Card, Conditional, Switch } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod/v4';

export const LINE_ITEM_PRICE_FORM_SCHEMA = z.object({
  predictedMaterialCost: z.number().optional(),
  markupTotals: z.object({
    markupType: z.enum(['profitPercentage', 'profitAmount', 'price']),
    profitPercentage: z.number(),
    profitAmount: z.number(),
    price: z.number(),
  }),
  autoUpdatePrice: z.boolean(),
});

export type TLineItemPriceCardFormState = z.infer<
  typeof LINE_ITEM_PRICE_FORM_SCHEMA
>;

type TProps = {
  hasLinkedMaterials: boolean;
  readonlyMarkupValues?: boolean;
};

const LineItemPriceCard: FC<TProps> = (props) => {
  const { hasLinkedMaterials, readonlyMarkupValues } = props;

  /**
   * Queries
   */
  const { data: regionPreferences, isLoading: isLoadingRegionPreferences } =
    useGetMyRegionPreferences();

  const formMethods = useFormContext<TLineItemPriceCardFormState>();

  const { markupType } = formMethods.watch('markupTotals');
  const predictedMaterialCost = formMethods.watch('predictedMaterialCost');

  if (isLoadingRegionPreferences || !regionPreferences) {
    return null;
  }

  return (
    <>
      <Conditional condition={hasLinkedMaterials}>
        <Card>
          <Card.Body>
            <div className="flex justify-between">
              <label>Materials (ex Tax)</label>

              <p>
                <CurrencyValue
                  value={predictedMaterialCost || 0}
                  currency={regionPreferences.currency}
                />
              </p>
            </div>
          </Card.Body>
        </Card>
      </Conditional>

      <Card>
        <Card.Body>
          <Controller
            control={formMethods.control}
            name="markupTotals"
            render={({ field: { onChange, value } }) => {
              return (
                <LineItemPriceField
                  markupTotals={value}
                  hasLinkedMaterials={hasLinkedMaterials}
                  predictedMaterialCost={predictedMaterialCost}
                  onMarkupTotalChange={onChange}
                  readonlyMarkupValues={readonlyMarkupValues}
                  currency={regionPreferences.currency}
                />
              );
            }}
          />
        </Card.Body>
      </Card>

      <Conditional condition={markupType !== 'price'}>
        <SwitchGroup>
          <SwitchLabel className="block">
            <Card>
              <Card.Body>
                <div className="flex justify-between">
                  <div>
                    <h4>Auto update</h4>

                    <p>When Material costs change update my sale price</p>
                  </div>

                  <Controller
                    name="autoUpdatePrice"
                    control={formMethods.control}
                    render={({ field: { onChange, value } }) => {
                      return <Switch value={value} onChange={onChange} />;
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </SwitchLabel>
        </SwitchGroup>
      </Conditional>
    </>
  );
};

export default LineItemPriceCard;
